import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertTriangle,
  BatteryMedium,
  Camera,
  CheckCircle2,
  Clock,
  Loader2,
  MapPin,
  Radio,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";

import { WorkspaceShell } from "@/components/dravian/workspace-shell";
import {
  devices as devicesApi,
  type ApiDeviceCommand,
  type ApiEntity,
} from "@/lib/api";

export const Route = createFileRoute("/$workspace/devices")({
  head: () => ({ meta: [{ title: "Devices — Dravian Horizon" }] }),
  component: DevicesPage,
});

/** A device is considered offline when its bridge hasn't reported in for
 *  this long. The bridge pushes telemetry every few seconds, so a minute of
 *  silence already means something is wrong. */
const OFFLINE_AFTER_MS = 60_000;

type DeviceStatus = "online" | "stale" | "offline";

function lastSeenMs(device: ApiEntity): number | null {
  const raw = (device.attributes ?? {}).last_seen;
  if (typeof raw !== "string") return null;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? null : t;
}

function statusOf(device: ApiEntity): DeviceStatus {
  const seen = lastSeenMs(device);
  if (seen === null) return "offline";
  const age = Date.now() - seen;
  if (age <= OFFLINE_AFTER_MS) return "online";
  if (age <= OFFLINE_AFTER_MS * 5) return "stale";
  return "offline";
}

const STATUS_STYLE: Record<DeviceStatus, { dot: string; label: string; text: string }> = {
  online: { dot: "bg-risk-low", label: "Online", text: "text-risk-low" },
  stale: { dot: "bg-risk-medium", label: "No recent telemetry", text: "text-risk-medium" },
  offline: { dot: "bg-muted-foreground", label: "Offline", text: "text-muted-foreground" },
};

function DevicesPage() {
  const { workspace: id } = useParams({ from: "/$workspace/devices" });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const devicesQ = useQuery({
    queryKey: ["devices", id],
    queryFn: () => devicesApi.list(id),
    // Devices are live hardware — poll so the operator sees a drone drop off
    // without having to reload the page.
    refetchInterval: 10_000,
  });

  const devices = devicesQ.data ?? [];
  const selected = useMemo(
    () => devices.find((d) => d.id === selectedId) ?? null,
    [devices, selectedId],
  );

  const counts = useMemo(() => {
    let online = 0;
    for (const d of devices) if (statusOf(d) === "online") online++;
    return { online, total: devices.length };
  }, [devices]);

  return (
    <WorkspaceShell>
      <div className="flex h-full min-h-0">
        <div className="flex-1 min-w-0 overflow-auto">
          <div className="p-5 max-w-[1000px] mx-auto space-y-4">
            <div>
              <div className="label-eyebrow mb-1">Devices</div>
              <h2 className="text-[20px] font-semibold">
                {devicesQ.isLoading
                  ? "Loading…"
                  : `${counts.online} of ${counts.total} online`}
              </h2>
              <p className="text-[12px] text-muted-foreground mt-1">
                Field devices reporting through a bridge. Registration and
                telemetry are pushed by the bridge; Horizon queues commands
                back for it to collect.
              </p>
            </div>

            {!devicesQ.isLoading && devices.length === 0 ? (
              <EmptyDevices workspaceId={id} />
            ) : (
              <div className="space-y-2">
                {devices.map((d) => (
                  <DeviceRow
                    key={d.id}
                    device={d}
                    selected={d.id === selectedId}
                    onSelect={() => setSelectedId(d.id === selectedId ? null : d.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {selected && (
          <DeviceDetail
            workspaceId={id}
            device={selected}
            onClose={() => setSelectedId(null)}
          />
        )}
      </div>
    </WorkspaceShell>
  );
}

function EmptyDevices({ workspaceId }: { workspaceId: string }) {
  const navigate = useNavigate();
  return (
    <div className="rounded-md border border-dashed border-border bg-surface/40 p-6 text-center">
      <Radio className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
      <div className="text-[14px] font-medium mb-1">No devices registered</div>
      <p className="text-[12px] text-muted-foreground max-w-md mx-auto">
        Devices appear here once a bridge registers them. Create a bridge
        token in Settings, put it in the bridge's config, and start it — the
        device registers itself on first contact.
      </p>
      <button
        onClick={() =>
          navigate({ to: "/$workspace/settings", params: { workspace: workspaceId } })
        }
        className="mt-3 rounded bg-primary/15 px-3 py-1.5 text-[12px] font-medium text-primary hover:bg-primary/25"
      >
        Create a bridge token
      </button>
    </div>
  );
}

function DeviceRow({
  device,
  selected,
  onSelect,
}: {
  device: ApiEntity;
  selected: boolean;
  onSelect: () => void;
}) {
  const status = statusOf(device);
  const style = STATUS_STYLE[status];
  const attrs = device.attributes ?? {};
  const battery = typeof attrs.battery_pct === "number" ? attrs.battery_pct : null;
  const lat = device.geolocation?.lat ?? device.lat ?? null;
  const lng = device.geolocation?.lng ?? device.lng ?? null;
  const seen = lastSeenMs(device);

  return (
    <button
      onClick={onSelect}
      className={`w-full rounded-md border px-3 py-2.5 text-left transition ${
        selected
          ? "border-primary/40 bg-primary/5"
          : "border-border bg-surface/50 hover:bg-surface-elevated"
      }`}
    >
      <div className="flex items-center gap-3">
        <span className={`h-2 w-2 rounded-full shrink-0 ${style.dot}`} />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-foreground truncate">
            {device.name}
          </div>
          <div className={`text-[11px] ${style.text}`}>
            {style.label}
            {seen !== null && status !== "online" && ` · ${relativeTime(seen)}`}
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground shrink-0">
          {battery !== null && (
            <span className="inline-flex items-center gap-1">
              <BatteryMedium className="h-3.5 w-3.5" />
              {Math.round(battery)}%
            </span>
          )}
          {lat != null && lng != null && (
            <span className="inline-flex items-center gap-1 font-mono">
              <MapPin className="h-3.5 w-3.5" />
              {lat.toFixed(3)}, {lng.toFixed(3)}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function DeviceDetail({
  workspaceId,
  device,
  onClose,
}: {
  workspaceId: string;
  device: ApiEntity;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const status = statusOf(device);

  const commandsQ = useQuery({
    queryKey: ["device-commands", workspaceId, device.id],
    queryFn: () => devicesApi.listCommands(workspaceId, device.id),
    // Poll while something is in flight so the operator watches the command
    // move pending -> delivered -> done without touching anything.
    refetchInterval: (query) => {
      const rows = (query.state.data ?? []) as ApiDeviceCommand[];
      return rows.some((c) => c.status === "pending" || c.status === "delivered")
        ? 3_000
        : false;
    },
  });

  const snapshot = useMutation({
    mutationFn: () => devicesApi.queueCommand(workspaceId, device.id, { type: "snapshot" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["device-commands", workspaceId, device.id] });
    },
  });

  const commands = commandsQ.data ?? [];
  const attrs = device.attributes ?? {};

  return (
    <div className="w-[380px] shrink-0 border-l border-border bg-surface/70 flex flex-col overflow-hidden">
      <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-border">
        <div className="min-w-0">
          <div className="label-eyebrow !mb-0.5">Device</div>
          <div className="text-[14px] font-semibold truncate">{device.name}</div>
          <div className={`text-[11px] ${STATUS_STYLE[status].text}`}>
            {STATUS_STYLE[status].label}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground shrink-0"
          title="Close"
        >
          <XCircle className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4">
        <div>
          <button
            onClick={() => snapshot.mutate()}
            disabled={snapshot.isPending || status === "offline"}
            title={
              status === "offline"
                ? "Device is offline — it won't collect the command until its bridge reconnects"
                : "Queue a snapshot for the bridge to collect"
            }
            className="w-full inline-flex items-center justify-center gap-2 rounded bg-primary/15 px-3 py-2 text-[12px] font-medium text-primary hover:bg-primary/25 disabled:opacity-40"
          >
            {snapshot.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Camera className="h-3.5 w-3.5" />
            )}
            Snapshot now
          </button>
          {snapshot.isError && (
            <div className="mt-1.5 text-[11px] text-risk-high">
              {snapshot.error instanceof Error
                ? snapshot.error.message
                : "Could not queue the command"}
            </div>
          )}
          <p className="mt-1.5 text-[10px] text-muted-foreground leading-relaxed">
            The command waits until the bridge next polls — it does not run
            instantly. The captured image arrives as a visual_evidence entity.
          </p>
        </div>

        <section>
          <div className="label-eyebrow !mb-1.5">Telemetry</div>
          <dl className="space-y-1 text-[11px]">
            <TelemetryRow label="Battery" value={fmtPct(attrs.battery_pct)} />
            <TelemetryRow label="Altitude" value={fmtNum(attrs.altitude_m, "m")} />
            <TelemetryRow label="Heading" value={fmtNum(attrs.heading_deg, "°")} />
            <TelemetryRow label="Speed" value={fmtNum(attrs.speed_mps, "m/s")} />
            <TelemetryRow
              label="Position"
              value={
                device.geolocation?.lat != null && device.geolocation?.lng != null
                  ? `${device.geolocation.lat.toFixed(5)}, ${device.geolocation.lng.toFixed(5)}`
                  : "—"
              }
            />
            <TelemetryRow
              label="Last seen"
              value={
                lastSeenMs(device) !== null ? relativeTime(lastSeenMs(device)!) : "never"
              }
            />
          </dl>
          {(device.geolocation?.lat != null || device.lat != null) && (
            <button
              onClick={() =>
                navigate({
                  to: "/$workspace/geographic",
                  params: { workspace: workspaceId },
                  search: (prev: Record<string, unknown>) => ({
                    ...prev,
                    focus: device.id,
                  }),
                })
              }
              className="mt-2 text-[11px] text-primary hover:underline"
            >
              Show on map
            </button>
          )}
        </section>

        <section>
          <div className="label-eyebrow !mb-1.5">
            Commands {commands.length > 0 && `(${commands.length})`}
          </div>
          {commandsQ.isLoading ? (
            <div className="text-[11px] text-muted-foreground">Loading…</div>
          ) : commands.length === 0 ? (
            <div className="text-[11px] text-muted-foreground italic">
              Nothing queued yet.
            </div>
          ) : (
            <div className="space-y-1.5">
              {commands.map((c) => (
                <CommandRow key={c.id} command={c} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function CommandRow({ command }: { command: ApiDeviceCommand }) {
  const { Icon, tone, label } = commandPresentation(command);
  return (
    <div className="rounded border border-border/70 bg-surface/50 px-2 py-1.5">
      <div className="flex items-center gap-1.5">
        <Icon className={`h-3 w-3 shrink-0 ${tone}`} />
        <span className="text-[11px] font-medium text-foreground/90">
          {command.type}
        </span>
        <span className={`text-[10px] ${tone}`}>{label}</span>
        <span className="ml-auto text-[9px] font-mono text-muted-foreground">
          {relativeTime(new Date(command.created_at).getTime())}
        </span>
      </div>
      {command.result && (
        <div className="mt-0.5 text-[10px] text-muted-foreground break-words">
          {command.result}
        </div>
      )}
    </div>
  );
}

function commandPresentation(c: ApiDeviceCommand): {
  Icon: React.ComponentType<{ className?: string }>;
  tone: string;
  label: string;
} {
  if (c.status === "done") {
    return { Icon: CheckCircle2, tone: "text-risk-low", label: "done" };
  }
  if (c.status === "failed") {
    return { Icon: XCircle, tone: "text-risk-high", label: "failed" };
  }
  // A delivered command that went quiet is the interesting failure mode:
  // the bridge took the work and never reported back.
  if (c.stale) {
    return { Icon: AlertTriangle, tone: "text-risk-medium", label: "no response" };
  }
  if (c.status === "delivered") {
    return { Icon: Loader2, tone: "text-primary", label: "running" };
  }
  return { Icon: Clock, tone: "text-muted-foreground", label: "waiting for bridge" };
}

function TelemetryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono text-foreground/90 truncate">{value}</dd>
    </div>
  );
}

function fmtPct(v: unknown): string {
  return typeof v === "number" ? `${Math.round(v)}%` : "—";
}
function fmtNum(v: unknown, unit: string): string {
  return typeof v === "number" ? `${Math.round(v * 10) / 10}${unit}` : "—";
}

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 0) return "just now";
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
